import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import Header from '../../components/ui/Header';
import {
  DollarSign,
  TrendingUp,
  CreditCard,
  Receipt,
  BarChart3,
  Search,
  Filter,
  Users,
  Warehouse,
  PanelLeftClose,
  PanelLeftOpen,
  ChevronDown,
  ChevronRight,
  PieChart,
  Activity,
  ArrowUpRight,
} from 'lucide-react';
import Button from '../../components/ui/Button';
import PayrollHR from './payroll-hr';
import Sales from './sales';
import SalesRegister from './sales-register';
import Purchases from './purchases';
import ExpenseRegister from './expense-register';
import { BankTransactions } from './payment-history';
import Utilities from './utilities';
import Contacts from "./contacts";
import Inventory from "./inventory";
import { API_ENDPOINTS } from '../../utils/config';

const FinanceTracker = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeSection, setActiveSection] = useState(() => {
    // Initialize from URL parameter or default to 'overview'
    return searchParams.get('section') || 'dashboard';
  });
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [expandedItems, setExpandedItems] = useState({});
  const isInitialMount = useRef(true);
  const [dashboardMetrics, setDashboardMetrics] = useState({
    purchaseInvoices: 0,
    purchaseOrders: 0,
    voucherPayments: 0,
    salaryPayments: 0,
    expenseRegister: 0,
    expensesCount: 0,
    expensesAmount: 0,
    salesInvoices: 0,
    expensesSummary: null,
    customers: 0,
    suppliers: 0,
    employees: 0,
    inventoryItems: 0,
    payrollRuns: 0,
    bankRecords: 0,
    utilities: 0,
    clients: 0,
  });
  const [isDashboardLoading, setIsDashboardLoading] = useState(false);
  const [dashboardError, setDashboardError] = useState('');

  const sidebarItems = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: 'BarChart3',
      path: '/finance-tracker/dashboard'
    },
    {
      id: 'contacts',
      label: 'Contacts',
      icon: 'Users',
      path: '/finance-tracker/contacts'
    },
    {
      id: 'inventory',
      label: 'Inventory',
      icon: 'Warehouse',
      path: '/finance-tracker/inventory'
    },
    {
      id: 'payroll',
      label: 'Payroll & HR',
      icon: 'Users',
      path: '/finance-tracker/payroll'
    },
    {
      id: 'invoices',
      label: 'Sales',
      icon: 'Receipt',
      path: '/finance-tracker/invoices',
      subItems: [
        {
          id: 'sales-register',
          label: 'Sales Register',
          path: '/finance-tracker/sales-register'
        }
      ]
    },
    {
      id: 'purchases',
      label: 'Purchases',
      icon: 'CreditCard',
      path: '/finance-tracker/purchases',
      subItems: [
        {
          id: 'expense-register',
          label: 'Expense Register',
          path: '/finance-tracker/expense-register'
        }
      ]
    },
    {
      id: 'payments',
      label: 'Bank Transactions',
      icon: 'CreditCard',
      path: '/finance-tracker/payments'
    },
    {
      id: 'utilities',
      label: 'Cashflow',
      icon: 'Receipt',
      path: '/finance-tracker/utilities'
    }
  ];

  // Initialize activeSection from URL on mount
  useEffect(() => {
    if (isInitialMount.current) {
      const sectionFromUrl = searchParams.get('section');
      if (sectionFromUrl) {
        setActiveSection(sectionFromUrl);
      } else {
        // If no section in URL, set default and update URL
        setSearchParams({ section: 'dashboard' }, { replace: true });
      }
      isInitialMount.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update URL when activeSection changes (after initial mount)
  useEffect(() => {
    if (!isInitialMount.current) {
      const sectionFromUrl = searchParams.get('section');
      if (activeSection && sectionFromUrl !== activeSection) {
        setSearchParams({ section: activeSection }, { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSection]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  };

  const normalizeCount = (data) => {
    if (Array.isArray(data)) return data.length;
    if (typeof data?.count === 'number') return data.count;
    if (data?.results && Array.isArray(data.results)) return data.results.length;
    if (data?.data && Array.isArray(data.data)) return data.data.length;
    return 0;
  };

  const fetchDashboardData = async () => {
    setIsDashboardLoading(true);
    setDashboardError('');
    try {
      const headers = getAuthHeaders();

      const listFetch = async (url) => {
        const res = await axios.get(url, { headers, params: { page: 1 } });
        return normalizeCount(res.data);
      };

      const [
        purchaseInvoices,
        purchaseOrders,
        voucherPayments,
        salaryPayments,
        expenseRegister,
        expensesSummaryRes,
        salesInvoices,
        customers,
        suppliers,
        employees,
        inventoryItems,
        payrollRuns,
        bankRecords,
        utilities,
        clients,
      ] = await Promise.all([
        listFetch(API_ENDPOINTS.PURCHASE_INVOICES_LIST),
        listFetch(API_ENDPOINTS.PURCHASE_ORDERS_LIST),
        listFetch(API_ENDPOINTS.VOUCHER_PAYMENTS_LIST),
        listFetch(API_ENDPOINTS.SALARY_PAYMENTS_LIST),
        listFetch(API_ENDPOINTS.EXPENSE_REGISTER_LIST),
        axios
          .get(API_ENDPOINTS.EXPENSES_SUMMARY, { headers })
          .then((res) => res.data)
          .catch(() => null),
        listFetch(API_ENDPOINTS.INVOICES_LIST),
        listFetch(API_ENDPOINTS.CUSTOMERS_LIST),
        listFetch(API_ENDPOINTS.SUPPLIERS_LIST),
        listFetch(API_ENDPOINTS.EMPLOYEES_LIST),
        listFetch(API_ENDPOINTS.SALES_CATALOG_LIST),
        listFetch(API_ENDPOINTS.PAYROLL_LIST),
        listFetch(API_ENDPOINTS.BANK_RECORDS_LIST),
        listFetch(API_ENDPOINTS.UTILITIES_LIST),
        listFetch(API_ENDPOINTS.CLIENTS_LIST),
      ]);

      setDashboardMetrics({
        purchaseInvoices,
        purchaseOrders,
        voucherPayments,
        salaryPayments,
        expenseRegister,
        expensesCount: expensesSummaryRes?.total_expenses || 0,
        expensesAmount: expensesSummaryRes?.total_amount || 0,
        salesInvoices,
        expensesSummary: expensesSummaryRes,
        customers,
        suppliers,
        employees,
        inventoryItems,
        payrollRuns,
        bankRecords,
        utilities,
        clients,
      });
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err?.response?.data?.detail ||
        err?.message ||
        'Failed to load dashboard data';
      setDashboardError(message);
    } finally {
      setIsDashboardLoading(false);
    }
  };

  // Auto-expand parent items when a sub-item is active
  useEffect(() => {
    const newExpandedItems = { ...expandedItems };
    let hasChanges = false;

    sidebarItems.forEach(item => {
      if (item.subItems && item.subItems.some(sub => sub.id === activeSection)) {
        if (!newExpandedItems[item.id]) {
          newExpandedItems[item.id] = true;
          hasChanges = true;
        }
      }
    });

    if (hasChanges) {
      setExpandedItems(newExpandedItems);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSection]);

  // Load dashboard metrics when on dashboard
  useEffect(() => {
    if (activeSection === 'dashboard') {
      fetchDashboardData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSection]);

  const toggleExpanded = (itemId) => {
    setExpandedItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  const renderSidebarItem = (item) => {
    const IconComponent = item.icon === 'BarChart3' ? BarChart3 :
      item.icon === 'TrendingUp' ? TrendingUp :
        item.icon === 'CreditCard' ? CreditCard :
          item.icon === 'Receipt' ? Receipt :
            item.icon === 'Users' ? Users :
              item.icon === 'Warehouse' ? Warehouse : DollarSign;

    const hasSubItems = item.subItems && item.subItems.length > 0;
    const isExpanded = expandedItems[item.id] || (hasSubItems && item.subItems.some(sub => sub.id === activeSection));
    const isActive = activeSection === item.id || (hasSubItems && item.subItems.some(sub => sub.id === activeSection));

    if (hasSubItems) {

      return (
        <div key={item.id} className="space-y-1">
          <button
            onClick={(e) => {
              e.stopPropagation();
              toggleExpanded(item.id);
              setActiveSection(item.id);
            }}
            className={`w-full flex items-center justify-between px-3 py-2 text-sm font-medium rounded-md transition-colors ${isActive
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}
            title={isSidebarCollapsed ? item.label : undefined}
          >
            <div className="flex items-center space-x-2">
              <IconComponent size={18} className="flex-shrink-0" />
              {!isSidebarCollapsed && <span className="truncate">{item.label}</span>}
            </div>
            {!isSidebarCollapsed && (
              isExpanded ? (
                <ChevronDown size={16} className="flex-shrink-0" />
              ) : (
                <ChevronRight size={16} className="flex-shrink-0" />
              )
            )}
          </button>
          {!isSidebarCollapsed && isExpanded && (
            <div className="ml-4 space-y-1">
              {item.subItems.map((subItem) => (
                <button
                  key={subItem.id}
                  onClick={() => setActiveSection(subItem.id)}
                  className={`w-full flex items-center space-x-2 px-3 py-2 text-sm font-medium rounded-md transition-colors ${activeSection === subItem.id
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                    }`}
                >
                  <span className="truncate">{subItem.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      );
    }

    return (
      <button
        key={item.id}
        onClick={() => setActiveSection(item.id)}
        className={`w-full flex items-center space-x-2 px-3 py-2 text-sm font-medium rounded-md transition-colors ${activeSection === item.id
            ? 'bg-primary text-primary-foreground'
            : 'text-muted-foreground hover:text-foreground hover:bg-muted'
          }`}
        title={isSidebarCollapsed ? item.label : undefined}
      >
        <IconComponent size={18} className="flex-shrink-0" />
        {!isSidebarCollapsed && <span className="truncate">{item.label}</span>}
      </button>
    );
  };

  function renderContent() {
    switch (activeSection) {
      case 'dashboard':
        return (
          <div className="space-y-6">
            {dashboardError && (
              <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-md">
                {dashboardError}
              </div>
            )}
            <div className="space-y-5">
              {/* Match sidebar sequence: Contacts, Inventory, Payroll & HR, Sales, Purchases, Payments, Utilities */}
              <Section title="Contacts">
                <StatCard
                  title="Clients"
                  value={dashboardMetrics.clients}
                  icon={Users}
                  accent="bg-blue-50 text-blue-700"
                  loading={isDashboardLoading}
                />
                <StatCard
                  title="Customers"
                  value={dashboardMetrics.customers}
                  icon={Users}
                  accent="bg-sky-100 text-sky-700"
                  loading={isDashboardLoading}
                />
                <StatCard
                  title="Suppliers"
                  value={dashboardMetrics.suppliers}
                  icon={Users}
                  accent="bg-cyan-100 text-cyan-700"
                  loading={isDashboardLoading}
                />
              </Section>

              <Section title="Inventory">
                <StatCard
                  title="Inventory Items"
                  value={dashboardMetrics.inventoryItems}
                  icon={Warehouse}
                  accent="bg-orange-100 text-orange-700"
                  loading={isDashboardLoading}
                />
              </Section>

              <Section title="Payroll & HR">
                <StatCard
                  title="Employees"
                  value={dashboardMetrics.employees}
                  icon={Users}
                  accent="bg-lime-100 text-lime-700"
                  loading={isDashboardLoading}
                />
                <StatCard
                  title="Payroll Runs"
                  value={dashboardMetrics.payrollRuns}
                  icon={CreditCard}
                  accent="bg-amber-100 text-amber-700"
                  loading={isDashboardLoading}
                />
                <StatCard
                  title="Salary Payments"
                  value={dashboardMetrics.salaryPayments}
                  icon={TrendingUp}
                  accent="bg-amber-100 text-amber-700"
                  loading={isDashboardLoading}
                />
              </Section>

              <Section title="Sales">
                <StatCard
                  title="Sales Invoices"
                  value={dashboardMetrics.salesInvoices}
                  icon={Activity}
                  accent="bg-purple-100 text-purple-700"
                  loading={isDashboardLoading}
                />
              </Section>

              <Section title="Purchases">
                <StatCard
                  title="Purchase Invoices"
                  value={dashboardMetrics.purchaseInvoices}
                  icon={Receipt}
                  accent="bg-blue-100 text-blue-700"
                  loading={isDashboardLoading}
                />
                <StatCard
                  title="Purchase Orders"
                  value={dashboardMetrics.purchaseOrders}
                  icon={CreditCard}
                  accent="bg-indigo-100 text-indigo-700"
                  loading={isDashboardLoading}
                />
                <StatCard
                  title="Expense Register"
                  value={dashboardMetrics.expenseRegister}
                  icon={PieChart}
                  accent="bg-teal-100 text-teal-700"
                  loading={isDashboardLoading}
                />
                <StatCard
                  title="Expenses (Count)"
                  value={dashboardMetrics.expensesCount}
                  subtitle={`Total: ₹${Number(dashboardMetrics.expensesAmount || 0).toLocaleString('en-IN', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}`}
                  icon={BarChart3}
                  accent="bg-rose-100 text-rose-700"
                  loading={isDashboardLoading}
                />
              </Section>

              <Section title="Payments & Banking">
                <StatCard
                  title="Voucher Payments"
                  value={dashboardMetrics.voucherPayments}
                  icon={DollarSign}
                  accent="bg-emerald-100 text-emerald-700"
                  loading={isDashboardLoading}
                />
                <StatCard
                  title="Bank Records"
                  value={dashboardMetrics.bankRecords}
                  icon={DollarSign}
                  accent="bg-slate-100 text-slate-700"
                  loading={isDashboardLoading}
                />
              </Section>

              <Section title="Utilities">
                <StatCard
                  title="Utilities"
                  value={dashboardMetrics.utilities}
                  icon={TrendingUp}
                  accent="bg-green-100 text-green-700"
                  loading={isDashboardLoading}
                />
              </Section>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <StatusBreakdown
                title="Expenses by Status"
                data={dashboardMetrics.expensesSummary?.by_status}
                total={dashboardMetrics.expensesSummary?.total_expenses || dashboardMetrics.expensesCount}
                loading={isDashboardLoading}
              />
              <TypeBreakdown
                title="Expenses by Type"
                data={dashboardMetrics.expensesSummary?.by_type}
                total={dashboardMetrics.expensesSummary?.total_expenses || dashboardMetrics.expensesCount}
                loading={isDashboardLoading}
              />
            </div>
          </div>
        );

      case 'contacts':
        return <Contacts />;

      case 'inventory':
        return <Inventory />;

      case 'payroll':
        return <PayrollHR />;

      case 'invoices':
        return <Sales />;

      case 'sales-register':
        return <SalesRegister />;

      case 'purchases':
        return <Purchases />;

      case 'expense-register':
        return <ExpenseRegister />;

      case 'payments':
        return <BankTransactions />;

      case 'utilities':
        return <Utilities />;

      default:
        return (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                <DollarSign className="w-8 h-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">
                {sidebarItems.find(item => item.id === activeSection)?.label || 'Finance Section'}
              </h3>
              <p className="text-muted-foreground">
                This section is under development. Select a different option from the sidebar.
              </p>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="flex pt-16">
        {/* Sidebar */}
        <div className={`${isSidebarCollapsed ? 'w-16' : 'w-64'} bg-card border-r border-border min-h-screen p-4 transition-all duration-300 relative`}>
          {/* Dock/Collapse Button */}
          <button
            onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
            className="absolute -right-3 top-20 z-10 bg-card border border-border rounded-full p-1.5 shadow-md hover:bg-muted transition-colors"
            aria-label={isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {isSidebarCollapsed ? (
              <PanelLeftOpen className="w-4 h-4 text-muted-foreground" />
            ) : (
              <PanelLeftClose className="w-4 h-4 text-muted-foreground" />
            )}
          </button>

          {!isSidebarCollapsed && (
            <div className="mb-6">
              <h2 className="text-lg font-semibold text-foreground mb-2">Finance Tracker</h2>
              <p className="text-sm text-muted-foreground">Manage your financial operations</p>
            </div>
          )}

          <div className="space-y-2">
            {sidebarItems.map(renderSidebarItem)}
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 p-6">
          {activeSection !== 'invoices' && activeSection !== 'purchases' && (
            <div className="mb-6">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-2xl font-bold text-foreground">
                    {(() => {
                      const item = sidebarItems.find(item => item.id === activeSection);
                      if (item) return item.label;
                      const subItem = sidebarItems.find(item => item.subItems?.find(sub => sub.id === activeSection));
                      if (subItem) {
                        const foundSub = subItem.subItems.find(sub => sub.id === activeSection);
                        return foundSub ? foundSub.label : 'Finance Overview';
                      }
                      return 'Finance Dashboard';
                    })()}
                  </h1>
                  <p className="text-muted-foreground">
                    {activeSection === 'dashboard'
                      ? 'Finance dashboard with quick KPIs'
                      : (() => {
                        const item = sidebarItems.find(item => item.id === activeSection);
                        if (item) return `Manage your ${item.label.toLowerCase()}`;
                        const subItem = sidebarItems.find(item => item.subItems?.find(sub => sub.id === activeSection));
                        if (subItem) {
                          const foundSub = subItem.subItems.find(sub => sub.id === activeSection);
                          return foundSub ? `Manage your ${foundSub.label.toLowerCase()}` : '';
                        }
                        return '';
                      })()
                    }
                  </p>
                </div>

                <div className="flex items-center space-x-3">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <input
                      type="text"
                      placeholder="Search finances..."
                      className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                  <Button variant="outline" size="sm">
                    <Filter className="w-4 h-4 mr-2" />
                    Filter
                  </Button>
                </div>
              </div>
            </div>
          )}

          {renderContent()}
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ title, value, subtitle, loading, icon: IconComponent, accent }) => (
  <div className="bg-white p-6 rounded-lg border border-gray-200 flex items-start justify-between">
    <div>
      <p className="text-sm font-medium text-muted-foreground">{title}</p>
      <p className="text-2xl font-bold text-foreground mt-1">{loading ? '...' : value}</p>
      {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
    </div>
    {IconComponent && (
      <div className={`p-3 rounded-full ${accent || 'bg-gray-100 text-gray-700'}`}>
        <IconComponent className="w-5 h-5" />
      </div>
    )}
  </div>
);

const StatusBreakdown = ({ title, data, total, loading }) => {
  const entries = data ? Object.entries(data) : [];
  return (
    <div className="bg-white p-6 rounded-lg border border-gray-200 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-muted-foreground">{title}</p>
          <p className="text-lg font-semibold text-foreground">{loading ? '...' : `${entries.length} statuses`}</p>
        </div>
      </div>
      <div className="space-y-3">
        {loading && <p className="text-sm text-muted-foreground">Loading...</p>}
        {!loading && entries.length === 0 && <p className="text-sm text-muted-foreground">No data</p>}
        {!loading &&
          entries.map(([key, val]) => {
            const count = val?.count || 0;
            const pct = total ? Math.min(100, Math.round((count / total) * 100)) : 0;
            return (
              <div key={key} className="space-y-1">
                <div className="flex items-center justify-between text-sm">
                  <span className="capitalize text-foreground">{key.replace(/_/g, ' ')}</span>
                  <span className="text-muted-foreground">{count} ({pct}%)</span>
                </div>
                <div className="w-full bg-gray-100 h-2 rounded-full overflow-hidden">
                  <div className="h-2 bg-primary rounded-full" style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
      </div>
    </div>
  );
};

const TypeBreakdown = ({ title, data, total, loading }) => {
  const entries = data ? Object.entries(data) : [];
  const sorted = entries.sort((a, b) => (b[1]?.total || 0) - (a[1]?.total || 0)).slice(0, 6);
  return (
    <div className="bg-white p-6 rounded-lg border border-gray-200 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-muted-foreground">{title}</p>
          <p className="text-lg font-semibold text-foreground">{loading ? '...' : `${sorted.length} types`}</p>
        </div>
        <ArrowUpRight className="w-4 h-4 text-muted-foreground" />
      </div>
      <div className="space-y-3">
        {loading && <p className="text-sm text-muted-foreground">Loading...</p>}
        {!loading && sorted.length === 0 && <p className="text-sm text-muted-foreground">No data</p>}
        {!loading &&
          sorted.map(([key, val]) => {
            const amount = val?.total || 0;
            const pct = total ? Math.min(100, Math.round((val?.count || 0 / total) * 100)) : 0;
            return (
              <div key={key} className="flex items-center justify-between text-sm">
                <div>
                  <p className="font-medium text-foreground capitalize">{key}</p>
                  <p className="text-xs text-muted-foreground">
                    ₹{Number(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                </div>
                <span className="text-muted-foreground text-xs">{pct}%</span>
              </div>
            );
          })}
      </div>
    </div>
  );
};

const Section = ({ title, children }) => (
  <div className="space-y-3">
    <div className="flex items-center justify-between">
      <h3 className="text-sm font-semibold text-foreground">{title}</h3>
      <div className="h-px flex-1 ml-3 bg-gray-200" />
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
      {children}
    </div>
  </div>
);

export default FinanceTracker;